import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { finApi, finErr } from "@/lib/financeApi";
import { btn, btnGold, btnGreen, btnRed, field } from "@/components/nds/windows/finance/parts";
import { InlineAction } from "@/components/nds/windows/finance/recordParts";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";

const TONE = { green: btnGreen, red: btnRed, gold: btnGold, blue: `${btn} border-sky-400/40 text-sky-300 hover:bg-sky-500/10` };

/** Config-driven transition buttons: { name, label, from[], sa, note, amount, tone }. */
export function WorkflowActions({ cfg, rec, onChanged }) {
  const isSuper = useIsSuper();
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const id = `${cfg.testid}-${rec.id}`;
  const run = async (a, val) => {
    setBusy(true);
    const body = a.amount ? (val ? { amount: Number(val) } : {}) : { note: val || "" };
    try { if (a.before) await a.before(rec); await (cfg.client || finApi).patch(`${cfg.api}/${rec.id}/${a.name}`, body); toast.success(`${a.label} · done`); setMode(null); onChanged(); }
    catch (e) { toast.error(finErr(e)); } finally { setBusy(false); }
  };
  const avail = cfg.actions.filter((a) => a.from.includes(rec.status) && (!a.sa || isSuper));
  if (!avail.length) return null;
  if (mode) {
    const a = avail.find((x) => x.name === mode);
    return <InlineAction id={id} mode={a.name} numeric={a.amount} placeholder={a.placeholder} optional={a.optionalNote} confirmClass={TONE[a.tone]} busy={busy}
      onConfirm={(v) => run(a, v)} onCancel={() => setMode(null)} />;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {avail.map((a) => (
        <button key={a.name} type="button" disabled={busy} data-testid={`${id}-${a.name}`} className={TONE[a.tone]}
          onClick={(e) => { e.stopPropagation(); if (a.note || a.amount) setMode(a.name); else run(a); }}>{a.label}</button>
      ))}
    </div>
  );
}

const today = () => new Date().toISOString().slice(0, 10);

/** Config-driven create form: fields [{ k, label, type: text|number|date|select|resident|staff, options }]. */
export function WorkflowForm({ cfg, onDone, onCancel, onPick, initial }) {
  const [f, setF] = useState(() => ({ ...Object.fromEntries(cfg.form.map((x) => [x.k, x.type === "date" && !x.optional ? today() : x.type === "select" ? x.options[0][0] : ""])), ...(initial ? { resident_id: initial.resident_id } : {}) }));
  const [opts, setOpts] = useState({ resident: [], staff: [], partner: [] });
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(null);
  const [extra, setExtra] = useState({});
  const [seed, setSeed] = useState(null);
  const fill = (rid, day) => {
    if (!cfg.prefill) return;
    if (!rid) { setHint(null); return; }
    cfg.prefill(rid, day).then(({ values, hint: h, seed: sd }) => {
      setSeed(sd || null);
      setF((s) => ({ ...s, ...Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v == null ? "" : String(v)])) }));
      setHint(h);
    }).catch(() => setHint(null));
  };
  useEffect(() => { if (initial) fill(initial.resident_id, f.checkin_date); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const latest = React.useRef(f);
  latest.current = f;
  useEffect(() => {
    if (!cfg.prefillEvent) return undefined;
    const again = () => fill(latest.current.resident_id, latest.current.checkin_date);
    window.addEventListener(cfg.prefillEvent, again);
    return () => window.removeEventListener(cfg.prefillEvent, again);
  }, [cfg.prefillEvent]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (cfg.form.some((x) => x.type === "resident")) (cfg.residentClient || finApi).get("/residents", { status: "active" }).then((d) => setOpts((o) => ({ ...o, resident: d.residents.map((r) => [r.id, `${r.first_name} ${r.last_name}`]) }))).catch(() => {});
    if (cfg.form.some((x) => x.type === "staff")) finApi.get("/session-payouts/staff").then((d) => setOpts((o) => ({ ...o, staff: d.staff.map((s) => [s.id, s.name || s.email]) }))).catch(() => {});
    cfg.form.filter((x) => x.type === "remote").forEach((x) => cfg.client.get(x.path).then((d) => setOpts((o) => ({ ...o, [x.k]: d.records.map(x.map) }))).catch(() => {}));
    const pf = cfg.form.find((x) => x.type === "partner");
    if (pf) cfg.client.get("/partners").then((d) => setOpts((o) => ({ ...o, partner: d.records.filter((p) => p.status !== "inactive" && (!pf.partnerTypes || pf.partnerTypes.includes(p.type))).map((p) => [p.id, `${p.first_name} ${p.last_name} · ${p.type.replace(/_/g, " ")}`]) }))).catch(() => {});
  }, [cfg.form, cfg.client]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...Object.fromEntries(cfg.form.filter((x) => f[x.k] !== "" || !x.optional).map((x) => [x.k, x.type === "number" ? Number(f[x.k]) : f[x.k]])), ...extra };
    try { await (cfg.client || finApi).post(cfg.api, body); toast.success(`${cfg.label} created`); onDone(); }
    catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid={`${cfg.testid}-form`} className="p-4 border border-nex-gold/30 rounded-sm space-y-3 bg-white/[0.02]">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {cfg.form.map((x) => {
          const set = (e) => {
            setF((s) => ({ ...s, [x.k]: e.target.value }));
            if (x.type === "resident") { onPick?.(e.target.value ? { resident_id: e.target.value, name: e.target.selectedOptions[0]?.text } : null); fill(e.target.value, f.checkin_date); }
            if (x.k === "checkin_date" && f.resident_id) fill(f.resident_id, e.target.value);
          };
          const common = { value: f[x.k], onChange: set, "data-testid": `${cfg.testid}-form-${x.k}`, className: `${field} w-full`, required: !x.optional };
          const list = x.type === "select" ? x.options : x.type === "remote" ? opts[x.k] || [] : opts[x.type];
          return (
            <label key={x.k} className={`space-y-1 block ${x.wide ? "md:col-span-2" : ""}`}><span className="nds-label-dim text-[9px]">{x.label}</span>
              {list ? (
                <select {...common}>{x.type !== "select" && <option value="">{x.optional ? (x.type === "staff" ? "Me (default)" : "None") : "Select…"}</option>}{list.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
              ) : <input {...common} type={x.type} min={x.type === "number" ? "0.01" : undefined} step={x.type === "number" ? "0.01" : undefined} />}
            </label>
          );
        })}
      </div>
      {cfg.FormExtra && f.resident_id && <cfg.FormExtra key={`${f.resident_id}-${seed?.submitted_at || ""}`} seed={seed} onChange={setExtra} />}
      {hint && <div data-testid={`${cfg.testid}-form-hint`} className="space-y-0.5 text-[11px] text-nex-muted">{hint.map((h) => <div key={h} className={h.startsWith("!") ? "text-amber-300" : ""}>{h.replace(/^!/, "")}</div>)}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid={`${cfg.testid}-form-cancel`} className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Cancel</button>
        <button type="submit" disabled={busy} data-testid={`${cfg.testid}-form-submit`} className={btnGold}>Create</button>
      </div>
    </form>
  );
}
